import { DateTime } from 'luxon'
import type { TGoalReview } from '../components/goals/goalReviewRules'
import type { TGoal, TGoalReviewEntry } from '../components/goals/goalTypes'

type TReviewAnswer = Pick<TGoalReviewEntry, 'read' | 'fix' | 'stepTaskId' | 'stepDay' | 'skipped'>

/**
 * An in-memory stand-in for backend/api/goal_review.go, shared by walkthrough mode and the Cypress
 * API stub so the two cannot drift from each other. It keeps the parts of the contract the UI
 * depends on: one review week keyed by its Sunday, one replaceable answer per goal, the step's
 * title snapshotted when it is saved, and reads joined onto live goals only.
 *
 * It models a single week. `now` is injected because a spec freezes the app's clock, and the week
 * the fake reports has to be the week the app believes it is in.
 */
export const createGoalReviewFake = (now: () => DateTime = () => DateTime.local()) => {
    let dismissed = false
    let entries: TGoalReviewEntry[] = []

    const weekStart = () => {
        const today = now().startOf('day')
        // Luxon weekdays run Monday = 1 … Sunday = 7; the review week starts on Sunday.
        return today.minus({ days: today.weekday % 7 })
    }

    const current = (): TGoalReview => ({
        weekStart: weekStart().toISODate(),
        nextReview: weekStart().plus({ weeks: 1 }).toISODate(),
        dismissed,
        entries: entries.map((entry) => ({ ...entry })),
    })

    return {
        current,
        dismiss: (): TGoalReview => {
            dismissed = true
            return current()
        },
        save: (goalId: string, answer: TReviewAnswer, stepTitle?: string): TGoalReview => {
            const entry: TGoalReviewEntry = { goalId, ...answer, at: now().toISO() }
            if (answer.stepTaskId && stepTitle) entry.stepTitle = stepTitle
            entries = [...entries.filter((saved) => saved.goalId !== goalId), entry]
            return current()
        },
        /** The join GET /goals/ performs: this week's read first, then any seeded earlier ones. */
        attach: (goals: TGoal[]) => {
            const week = weekStart().toISODate()
            for (const goal of goals) {
                const entry = entries.find((saved) => saved.goalId === goal.id)
                const isArchiveAnswer = entry?.fix === 'archive' || entry?.fix === 'archive_done'
                if (goal.archivedAt || isArchiveAnswer) {
                    delete goal.reviews
                    continue
                }
                const earlier = (goal.reviews ?? []).filter((saved) => saved.weekStart !== week)
                const reviews = entry ? [{ ...entry, weekStart: week }, ...earlier] : earlier
                if (reviews.length > 0) goal.reviews = reviews
                else delete goal.reviews
            }
        },
        reset: () => {
            dismissed = false
            entries = []
        },
    }
}
